package com.arena_web.data.repository

import com.arena_web.data.api.ClubAdminApi
import com.arena_web.data.dto.*
import com.arena_web.domain.repositories.ClubAdminRepository

class ClubAdminRepositoryImpl(private val clubAdminApi: ClubAdminApi) : ClubAdminRepository {

    // Club CRUD
    override suspend fun uploadClubImage(fileBytes: ByteArray, fileName: String, contentType: String): Result<ImageUploadResponse> =
        apiCall { clubAdminApi.uploadClubImage(fileBytes, fileName, contentType) }

    override suspend fun createClub(request: CreateClubRequest): Result<ClubDto> =
        apiCall { clubAdminApi.createClub(request) }

    override suspend fun getMyClubs(): Result<List<ClubDto>> =
        apiCall { clubAdminApi.getMyClubs() }

    override suspend fun getClub(clubId: String): Result<ClubDto> =
        apiCall { clubAdminApi.getClub(clubId) }

    override suspend fun updateClub(clubId: String, request: UpdateClubRequest): Result<ClubDto> =
        apiCall { clubAdminApi.updateClub(clubId, request) }

    override suspend fun updateGallery(clubId: String, request: UpdateClubGalleryRequest): Result<List<ClubGalleryImageDto>> =
        apiCall { clubAdminApi.updateGallery(clubId, request) }

    override suspend fun updateRules(clubId: String, request: UpdateClubRulesRequest): Result<List<ClubRuleDto>> =
        apiCall { clubAdminApi.updateRules(clubId, request) }

    override suspend fun deleteClub(clubId: String): Result<Unit> =
        apiCall { clubAdminApi.deleteClub(clubId) }.map { }

    // Fields
    override suspend fun getFields(clubId: String): Result<List<FieldDto>> =
        apiCall { clubAdminApi.getFields(clubId) }

    override suspend fun createField(clubId: String, request: CreateFieldRequest): Result<FieldDto> =
        apiCall { clubAdminApi.createField(clubId, request) }

    override suspend fun updateField(clubId: String, fieldId: String, request: UpdateFieldRequest): Result<FieldDto> =
        apiCall { clubAdminApi.updateField(clubId, fieldId, request) }

    override suspend fun deleteField(clubId: String, fieldId: String): Result<Unit> =
        apiCallUnit { clubAdminApi.deleteField(clubId, fieldId) }

    // Schedule
    override suspend fun getSchedule(clubId: String): Result<ClubScheduleDto> =
        apiCall { clubAdminApi.getSchedule(clubId) }

    override suspend fun updateWorkingHours(clubId: String, request: UpdateWorkingHoursRequest): Result<Unit> =
        apiCallUnit { clubAdminApi.updateWorkingHours(clubId, request) }

    override suspend fun createScheduleException(clubId: String, request: CreateScheduleExceptionRequest): Result<ScheduleExceptionDto> =
        apiCall { clubAdminApi.createScheduleException(clubId, request) }

    override suspend fun updateScheduleException(clubId: String, exceptionId: String, request: UpdateScheduleExceptionRequest): Result<ScheduleExceptionDto> =
        apiCall { clubAdminApi.updateScheduleException(clubId, exceptionId, request) }

    override suspend fun deleteScheduleException(clubId: String, exceptionId: String): Result<Unit> =
        apiCallUnit { clubAdminApi.deleteScheduleException(clubId, exceptionId) }

    // Field Pricing
    override suspend fun getFieldPricing(clubId: String, fieldId: String): Result<List<FieldPricingDto>> =
        apiCall { clubAdminApi.getFieldPricing(clubId, fieldId) }

    override suspend fun updateFieldPricing(clubId: String, fieldId: String, request: UpdateFieldPricingRequest): Result<Unit> =
        apiCallUnit { clubAdminApi.updateFieldPricing(clubId, fieldId, request) }

    // Services
    override suspend fun getServices(clubId: String): Result<List<ClubServiceDto>> =
        apiCall { clubAdminApi.getServices(clubId) }

    override suspend fun createService(clubId: String, request: CreateClubServiceRequest): Result<ClubServiceDto> =
        apiCall { clubAdminApi.createService(clubId, request) }

    override suspend fun updateService(clubId: String, serviceId: String, request: UpdateClubServiceRequest): Result<ClubServiceDto> =
        apiCall { clubAdminApi.updateService(clubId, serviceId, request) }

    override suspend fun deleteService(clubId: String, serviceId: String): Result<Unit> =
        apiCallUnit { clubAdminApi.deleteService(clubId, serviceId) }

    // Amenities
    override suspend fun getAmenities(clubId: String): Result<List<String>> =
        apiCall { clubAdminApi.getAmenities(clubId) }

    override suspend fun updateAmenities(clubId: String, amenities: List<String>): Result<List<String>> =
        apiCall { clubAdminApi.updateAmenities(clubId, UpdateClubAmenitiesRequest(amenities)) }

    // Dashboard
    override suspend fun getDashboardStats(clubId: String, params: Map<String, String>): Result<DashboardStatsDto> =
        apiCall { clubAdminApi.getDashboardStats(clubId, params) }

    override suspend fun getDashboardChart(clubId: String, params: Map<String, String>): Result<DashboardChartDto> =
        apiCall { clubAdminApi.getDashboardChart(clubId, params) }

    // Bookings (Admin)
    override suspend fun getBookingCalendar(clubId: String, params: Map<String, String>): Result<BookingCalendarDto> =
        apiCall { clubAdminApi.getBookingCalendar(clubId, params) }

    override suspend fun getPendingBookings(clubId: String): Result<List<BookingDto>> =
        apiCall { clubAdminApi.getPendingBookings(clubId) }

    override suspend fun adminCreateBooking(clubId: String, request: AdminCreateBookingRequest): Result<BookingDto> =
        apiCall { clubAdminApi.adminCreateBooking(clubId, request) }

    override suspend fun adminEditBooking(clubId: String, bookingId: String, request: EditBookingRequest): Result<BookingDto> =
        apiCall { clubAdminApi.adminEditBooking(clubId, bookingId, request) }

    override suspend fun approveBooking(clubId: String, bookingId: String): Result<BookingDto> =
        apiCall { clubAdminApi.approveBooking(clubId, bookingId) }

    override suspend fun rejectBooking(clubId: String, bookingId: String): Result<BookingDto> =
        apiCall { clubAdminApi.rejectBooking(clubId, bookingId) }

    override suspend fun markBookingPaid(clubId: String, bookingId: String): Result<BookingDto> =
        apiCall { clubAdminApi.markBookingPaid(clubId, bookingId) }

    override suspend fun markBookingUnpaid(clubId: String, bookingId: String): Result<BookingDto> =
        apiCall { clubAdminApi.markBookingUnpaid(clubId, bookingId) }

    override suspend fun cancelBooking(clubId: String, bookingId: String): Result<BookingDto> =
        apiCall { clubAdminApi.cancelBooking(clubId, bookingId) }

    override suspend fun startBooking(clubId: String, bookingId: String): Result<BookingDto> =
        apiCall { clubAdminApi.startBooking(clubId, bookingId) }

    override suspend fun completeBooking(clubId: String, bookingId: String): Result<BookingDto> =
        apiCall { clubAdminApi.completeBooking(clubId, bookingId) }

    // Clients
    override suspend fun getClients(clubId: String, params: Map<String, String>): Result<ClubClientsPageDto> =
        apiCall { clubAdminApi.getClients(clubId, params) }

    override suspend fun getClient(clubId: String, clientId: String): Result<ClubClientCardDto> =
        apiCall { clubAdminApi.getClient(clubId, clientId) }

    override suspend fun getClientBookings(clubId: String, clientId: String): Result<List<BookingDto>> =
        apiCall { clubAdminApi.getClientBookings(clubId, clientId) }.map { it.bookings }

    override suspend fun getClientDebts(clubId: String, clientId: String): Result<List<BookingDto>> =
        apiCall { clubAdminApi.getClientDebts(clubId, clientId) }

    override suspend fun addClientNote(clubId: String, clientId: String, request: AddClientNoteRequest): Result<ClubClientNoteDto> =
        apiCall { clubAdminApi.addClientNote(clubId, clientId, request) }

    override suspend fun deleteClientNote(clubId: String, clientId: String, noteId: String): Result<Unit> =
        apiCallUnit { clubAdminApi.deleteClientNote(clubId, clientId, noteId) }

    override suspend fun blockClient(clubId: String, clientId: String, request: BlockClientRequest): Result<Unit> =
        apiCallUnit { clubAdminApi.blockClient(clubId, clientId, request) }

    override suspend fun unblockClient(clubId: String, clientId: String): Result<Unit> =
        apiCallUnit { clubAdminApi.unblockClient(clubId, clientId) }

    // Feed
    override suspend fun getFeed(clubId: String): Result<List<ClubPostDto>> =
        apiCall { clubAdminApi.getFeed(clubId) }.map { it.items }

    override suspend fun getFeedPaged(clubId: String, params: Map<String, String>): Result<ClubPostPageDto> =
        apiCall { clubAdminApi.getFeed(clubId, params) }

    override suspend fun createPost(clubId: String, request: CreateClubPostRequest): Result<ClubPostDto> =
        apiCall { clubAdminApi.createPost(clubId, request) }

    override suspend fun deletePost(clubId: String, postId: String): Result<Unit> =
        apiCallUnit { clubAdminApi.deletePost(clubId, postId) }

    override suspend fun likePost(clubId: String, postId: String): Result<Unit> =
        apiCallUnit { clubAdminApi.likePost(clubId, postId) }

    override suspend fun createReview(clubId: String, request: CreateClubReviewRequest): Result<ClubReviewDto> =
        apiCall { clubAdminApi.createReview(clubId, request) }
}
